<template>
  <div>
    <transition
      mode="out-in"
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0 translate-y-2"
      leave-active-class="transition duration-200 ease-in"
      leave-to-class="opacity-0 translate-y-2"
    >
    <!-- Main Hydration Status Bar (only shown during initial load of the expert list) -->
    <status-bar
      v-if="
        (loadingAuthors || question.authorsStatus?.isLoading) && !loadedAuthors
      "
      key="loading"
      :total-step-count="totalStepCount"
      :current-step-number="currentStepNumber"
      :status="status || question.authorsStatus?.message"
      :substatus="substatus"
      :began-at="question.authorsStatus?.beganAt"
      theme="emerald"
      @cancel="$emit('cancel-loading', 'authors')"
    />
    <div
      v-else-if="!loadedAuthors"
      key="load-action"
      class="text-center w-full py-8"
    >
      <!-- Reusable Decoupled Error Card -->
      <research-compass-error-card
        v-if="isFailed"
        title="Expert Identification Failed"
        description="An unexpected error occurred while analyzing and identifying candidate experts."
        button-text="Retry Expert Search"
        button-class="bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-500 dark:hover:bg-emerald-600"
        @retry="getAuthorEvaluations()"
      />

      <!-- Standard Load Experts Action -->
      <template v-else>
        <button
          class="bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-500 dark:hover:bg-emerald-600 text-white px-6 py-2.5 uppercase text-sm font-bold rounded-xl transition-all shadow-lg flex items-center mx-auto gap-2"
          @click="getAuthorEvaluations()"
        >
          <i class="fas fa-user-graduate"></i>
          Load Experts
        </button>
        <div
          class="mt-4 flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400 text-xs font-semibold italic"
        >
          <i class="fa-regular fa-clock-three text-emerald-500/70"></i>
          Usually takes under 2 minutes
        </div>
      </template>
    </div>
    <div v-else-if="loadedAuthors" key="content">
      <div class="mb-8 min-h-[100px]">
        <!-- Explanation (Summary) View -->
        <div v-if="selectedSubTab === 'summary'">
          <div v-if="narrative && !generatingNarrative" class="mb-6">
            <div class="flex justify-end mb-2 gap-3">
              <button
                v-if="
                  !question.authors ||
                  Object.keys(question.authors).length === 0
                "
                class="text-[10px] font-bold uppercase tracking-wider text-gray-500 hover:text-emerald-600 transition-colors flex items-center gap-1.5"
                @click="getAuthorEvaluations()"
              >
                <i class="fas fa-arrows-rotate"></i>
                Retry Expert Search
              </button>
              <button
                class="text-[10px] font-bold uppercase tracking-wider text-gray-500 hover:text-emerald-600 transition-colors flex items-center gap-1.5"
                @click="clearNarrative()"
              >
                <i class="fas fa-arrows-rotate"></i>
                Regenerate Summary
              </button>
            </div>
            <research-compass-markdown-viewer
              :text="narrative"
              :question="question"
              ref-name="authorHolder"
              @scroll-to-citation="scrollToExpert"
            />
            <div class="flex justify-end mt-2">
              <feedback-modal
                sentiment="POSITIVE"
                item-type="AUTHOR_NARRATIVE"
                :item-id="question.hashedId"
                :ancillary-information-label="question.question"
                :research-compass-hashed-id="$route.params.researchCompassId"
              />
              <feedback-modal
                sentiment="NEGATIVE"
                item-type="AUTHOR_NARRATIVE"
                :item-id="question.hashedId"
                :ancillary-information-label="question.question"
                :research-compass-hashed-id="$route.params.researchCompassId"
              />
            </div>
          </div>
          <div v-else-if="generatingNarrative" class="py-10 px-6">
            <!-- Inline Synthesis Status Bar -->
            <status-bar
              :total-step-count="totalStepCount"
              :current-step-number="currentStepNumber"
              :status="status"
              :substatus="substatus"
              theme="emerald"
            />
          </div>
          <div
            v-else
            class="py-10 text-center bg-gray-50/50 dark:bg-gray-900/30 rounded-2xl border border-dashed border-gray-300 dark:border-gray-700"
          >
            <button
              class="bg-white dark:bg-gray-800 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-700 text-emerald-700 dark:text-emerald-400 px-6 py-2.5 uppercase text-xs font-bold rounded-xl transition-all shadow-sm flex items-center mx-auto gap-2"
              @click="generateNarrative()"
            >
              <i class="fas fa-wand-magic-sparkles"></i>
              Generate Expert Explanation
            </button>
            <div
              class="mt-4 flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400 text-xs font-semibold italic"
            >
              <i class="fa-regular fa-clock-three text-emerald-500/70"></i>
              Usually takes about a minute
            </div>
          </div>
        </div>

        <!-- Comparison (Table) View -->
        <div v-if="selectedSubTab === 'table'">
          <div v-if="generatingTable" class="py-10 px-6">
            <!-- Inline Synthesis Status Bar -->
            <status-bar
              :total-step-count="totalStepCount"
              :current-step-number="currentStepNumber"
              :status="status"
              :substatus="substatus"
              theme="emerald"
            />
          </div>
          <div
            v-else-if="!table"
            class="py-10 text-center bg-gray-50/50 dark:bg-gray-900/30 rounded-2xl border border-dashed border-gray-300 dark:border-gray-700"
          >
            <div
              v-if="
                !question.authors || Object.keys(question.authors).length === 0
              "
              class="text-center"
            >
              <i class="fas fa-info-circle text-gray-400 text-2xl mb-2"></i>
              <p class="font-bold text-gray-500 text-sm">
                No experts available to compare.
              </p>
            </div>
            <button
              v-else
              class="bg-white dark:bg-gray-800 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-700 text-emerald-700 dark:text-emerald-400 px-6 py-2.5 uppercase text-xs font-bold rounded-xl transition-all shadow-sm flex items-center mx-auto gap-2"
              @click="generateTable()"
            >
              <i class="fas fa-wand-magic-sparkles"></i>
              Generate Comparison Table
            </button>
            <div
              class="mt-4 flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400 text-xs font-semibold italic"
            >
              <i class="fa-regular fa-clock-three text-emerald-500/70"></i>
              Usually takes about a minute
            </div>
          </div>
          <div v-else class="overflow-x-auto pb-4 custom-scrollbar">
            <research-compass-markdown-viewer
              :text="table"
              :question="question"
              ref-name="authorHolder"
              :show-table-scroll-indicators="true"
              @scroll-to-citation="scrollToExpert"
            />
            <div class="flex justify-end mt-4">
              <feedback-modal
                sentiment="POSITIVE"
                item-type="AUTHOR_TABLE"
                :item-id="question.hashedId"
                :ancillary-information-label="question.question"
                :research-compass-hashed-id="$route.params.researchCompassId"
              />
              <feedback-modal
                sentiment="NEGATIVE"
                item-type="AUTHOR_TABLE"
                :item-id="question.hashedId"
                :ancillary-information-label="question.question"
                :research-compass-hashed-id="$route.params.researchCompassId"
              />
            </div>
          </div>
        </div>

        <!-- List View -->
        <div v-if="selectedSubTab === 'list'" class="w-full">
          <div
            v-if="
              !question.authors || Object.keys(question.authors).length === 0
            "
            class="py-10 text-center bg-gray-50/50 dark:bg-gray-900/30 rounded-2xl border border-dashed border-gray-300 dark:border-gray-700"
          >
            <div class="text-center mb-6">
              <i class="fas fa-info-circle text-gray-400 text-3xl mb-3"></i>
              <p class="font-bold text-gray-600 dark:text-gray-400 text-sm">
                No research experts were found for this query.
              </p>
              <p class="text-gray-500 dark:text-gray-500 text-xs mt-2">
                Click below to try the search again.
              </p>
            </div>
            <button
              class="bg-white dark:bg-gray-800 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-700 text-emerald-700 dark:text-emerald-400 px-6 py-2.5 uppercase text-xs font-bold rounded-xl transition-all shadow-sm flex items-center mx-auto gap-2"
              @click="getAuthorEvaluations()"
            >
              <i class="fas fa-arrows-rotate"></i>
              Retry Expert Search
            </button>
          </div>
          <template v-else-if="orderedAuthorsRecommended.length > 0">
            <div
              v-for="(author, index) in orderedAuthorsRecommended"
              :key="author.name"
              class="mb-4 flex items-start w-full"
            >
              <author-card
                :ref="`expert${index + 1}`"
                :question="question"
                :author="author"
                :show-detail="true"
                :include-researchers="includeResearchers"
                class="w-full scroll-mt-[150px]"
              />
            </div>
            <div class="flex items-center justify-center my-6">
              <div class="h-px bg-gray-300 dark:bg-gray-800 flex-grow"></div>
              <span
                class="mx-4 cursor-pointer text-sm font-bold text-gray-500 dark:text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors flex items-center"
                @click="toggleAllAuthorsExpanded()"
              >
                <i
                  :class="{ 'rotate-90': allAuthorsExpanded }"
                  class="fa-solid fa-caret-right mr-2 transition-transform duration-200"
                />
                {{ allAuthorsExpanded ? "Hide" : "Show" }} all experts
              </span>
              <div class="h-px bg-gray-300 dark:bg-gray-800 flex-grow"></div>
            </div>
            <div v-if="loadedAuthors && allAuthorsExpanded">
              <div
                v-for="(author, index) in orderedAuthorsNotRecommended"
                :key="author.name"
                class="mb-4 opacity-80 flex items-start w-full"
              >
                <author-card
                  :ref="`expert${orderedAuthorsRecommended.length + index + 1}`"
                  :question="question"
                  :author="author"
                  :show-detail="true"
                  :include-researchers="includeResearchers"
                  class="w-full scroll-mt-[150px]"
                />
              </div>
            </div>
          </template>
          <template v-else>
            <div
              v-for="(author, index) in orderedAuthorsNotRecommended"
              :key="author.name"
              class="mb-4 flex items-start w-full"
            >
              <author-card
                :ref="`expert${index + 1}`"
                :question="question"
                :author="author"
                :show-detail="true"
                :include-researchers="includeResearchers"
                class="w-full scroll-mt-[150px]"
              />
            </div>
          </template>
        </div>

        <!-- What About [X]? View -->
        <div v-if="selectedSubTab === 'missing'">
          <div
            class="bg-white/40 dark:bg-[#0d1a20]/40 backdrop-blur-md border border-gray-200 dark:border-gray-800 rounded-3xl p-8 shadow-sm"
          >
            <div class="max-w-2xl mx-auto">
              <div v-if="!selectedMissingAuthor">
                <div class="text-center mb-8">
                  <div
                    class="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 mb-4 shadow-inner"
                  >
                    <i class="fas fa-user-graduate text-2xl"></i>
                  </div>
                  <h3
                    class="text-2xl font-headline font-bold text-gray-900 dark:text-white mb-2"
                  >
                    What About [Researcher]?
                  </h3>
                  <p class="text-gray-500 dark:text-gray-400 text-sm">
                    Enter a researcher's name to see how they compare against
                    our top recommendations.
                  </p>
                </div>

                <!-- Search Box -->
                <form
                  class="relative group mb-8"
                  @submit.prevent="searchMissingAuthor()"
                >
                  <div
                    class="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none"
                  >
                    <i
                      class="fas fa-search text-gray-400 group-focus-within:text-emerald-500 transition-colors"
                    ></i>
                  </div>
                  <input
                    v-model="missingAuthorSearchQuery"
                    type="text"
                    class="w-full pl-12 pr-32 py-4 bg-white dark:bg-[#050a0c] border-2 border-gray-200 dark:border-gray-800 rounded-2xl text-gray-900 dark:text-white focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 outline-none transition-all shadow-sm group-hover:shadow-md"
                    placeholder="e.g. Yoshua Bengio or 1234567"
                  />
                  <div class="absolute inset-y-2 right-2">
                    <button
                      type="submit"
                      class="h-full px-6 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold rounded-xl shadow-lg shadow-emerald-500/20 transition-all flex items-center gap-2"
                      :disabled="isSearchingMissingAuthor"
                    >
                      <i
                        v-if="isSearchingMissingAuthor"
                        class="fas fa-circle-notch fa-spin"
                      ></i>
                      Search
                    </button>
                  </div>
                </form>

                <!-- Search Results Dropdown -->
                <div
                  v-if="missingAuthorSearchResults.length > 0"
                  class="bg-white dark:bg-[#050a0c] border border-gray-200 dark:border-gray-800 rounded-2xl shadow-xl overflow-hidden mb-8 max-h-96 overflow-y-auto"
                >
                  <div
                    v-for="result in missingAuthorSearchResults"
                    :key="result.authorId"
                    class="p-4 border-b border-gray-100 dark:border-gray-800 last:border-0 hover:bg-gray-50 dark:hover:bg-white/5 cursor-pointer transition-colors flex items-start justify-between gap-4 group"
                    @click="selectMissingAuthor(result)"
                  >
                    <div class="flex-grow min-w-0">
                      <p
                        class="font-bold text-gray-900 dark:text-white group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors"
                      >
                        {{ result.name }}
                      </p>
                      <p class="text-xs text-gray-500 truncate mt-0.5">
                        {{ result.affiliation || "No affiliation" }}
                      </p>
                      <div class="flex flex-wrap gap-2 mt-2">
                        <span
                          v-if="result.minYear && result.maxYear"
                          class="text-[10px] text-gray-500 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded"
                        >
                          <i class="fas fa-calendar-alt mr-1"></i>
                          Active: {{ result.minYear }} - {{ result.maxYear }}
                        </span>
                        <span class="text-[10px] text-gray-400 font-medium"
                          ><i class="fas fa-file-alt mr-1"></i>
                          {{ result.papercount }} Papers</span
                        >
                        <span class="text-[10px] text-gray-400 font-medium"
                          ><i class="fas fa-quote-right mr-1"></i>
                          {{ result.citationcount }} Citations</span
                        >
                      </div>

                      <div
                        v-if="
                          result.recentPapers && result.recentPapers.length > 0
                        "
                        class="mt-3 text-[10px] text-gray-600 dark:text-gray-400"
                      >
                        <div
                          class="font-bold text-gray-500 uppercase tracking-wider text-[8px] mb-1"
                        >
                          Recent Papers
                        </div>
                        <ul class="list-disc pl-4 space-y-0.5">
                          <li
                            v-for="paper in result.recentPapers"
                            :key="paper.title"
                            class="line-clamp-1"
                          >
                            {{ paper.title }}
                            <span class="text-gray-500"
                              >({{ paper.year }})</span
                            >
                          </li>
                        </ul>
                      </div>
                    </div>
                    <button
                      class="px-3 py-1.5 bg-gray-100 dark:bg-gray-800 text-[10px] font-bold rounded-lg text-gray-600 dark:text-gray-400 group-hover:bg-emerald-50 group-hover:text-emerald-600 dark:group-hover:bg-emerald-900/30 dark:group-hover:text-emerald-400 transition-all"
                    >
                      Select
                    </button>
                  </div>
                </div>

                <!-- Search Status -->
                <div
                  v-else-if="missingAuthorSearchStatus"
                  class="text-center py-4 text-gray-500 italic text-sm"
                >
                  {{ missingAuthorSearchStatus }}
                </div>
              </div>
              <!-- Evaluation Result -->
              <div v-if="isEvaluatingMissingAuthor" class="py-12">
                <status-bar
                  :total-step-count="totalStepCount"
                  :current-step-number="currentStepNumber"
                  :status="status"
                  :substatus="substatus"
                  theme="emerald"
                />
                <div class="flex justify-center mt-8">
                  <button
                    class="text-xs text-gray-500 hover:text-gray-700 font-bold flex items-center gap-2 px-4 py-2 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-all"
                    @click="
                      isEvaluatingMissingAuthor = false;
                      selectedMissingAuthor = null;
                      missingAuthorEvaluation = null;
                    "
                  >
                    <i class="fas fa-times"></i>
                    Cancel evaluation
                  </button>
                </div>
              </div>

              <div v-if="missingAuthorEvaluation" class="space-y-6">
                <div
                  class="bg-emerald-50/50 dark:bg-emerald-900/10 border border-emerald-100 dark:border-emerald-900/30 rounded-2xl p-6"
                >
                  <div class="flex items-center justify-between mb-4">
                    <div class="flex items-center gap-3">
                      <div
                        class="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 flex items-center justify-center font-bold text-lg"
                      >
                        {{ missingAuthorEvaluation.score }}/4
                      </div>
                      <div>
                        <h4
                          class="font-headline font-bold text-gray-900 dark:text-white"
                        >
                          {{ selectedMissingAuthor.name }}
                        </h4>
                        <p
                          class="text-[10px] text-emerald-600 uppercase font-bold"
                        >
                          Suitability Score
                        </p>
                      </div>
                    </div>
                    <div class="text-right">
                      <div class="text-xs font-bold text-gray-500 mb-1">
                        Concept Fitness
                      </div>
                      <div
                        class="inline-flex gap-1 items-center px-2 py-0.5 bg-white dark:bg-[#050a0c] border border-emerald-200 dark:border-emerald-800 rounded-lg"
                      >
                        <i
                          v-for="i in 4"
                          :key="i"
                          class="fas fa-star text-[8px]"
                          :class="
                            i <= missingAuthorEvaluation.concept_score
                              ? 'text-amber-400'
                              : 'text-gray-200 dark:text-gray-800'
                          "
                        ></i>
                      </div>
                    </div>
                  </div>

                  <div class="space-y-4">
                    <div>
                      <h5
                        class="text-[10px] uppercase tracking-widest font-bold text-gray-400 mb-2 flex items-center gap-2"
                      >
                        <i
                          class="fas fa-scale-balanced text-emerald-500/70"
                        ></i>
                        Comparison Rationale
                      </h5>
                      <p
                        class="text-sm text-gray-700 dark:text-gray-300 leading-relaxed italic"
                      >
                        "{{ missingAuthorEvaluation.comparison_rationale }}"
                      </p>
                    </div>

                    <div
                      class="pt-4 border-t border-emerald-100 dark:border-emerald-900/30"
                    >
                      <h5
                        class="text-[10px] uppercase tracking-widest font-bold text-gray-400 mb-2 flex items-center gap-2"
                      >
                        <i class="fas fa-check-circle text-emerald-500/70"></i>
                        Suitability Summary
                      </h5>
                      <p
                        class="text-sm text-gray-700 dark:text-gray-300 leading-relaxed"
                      >
                        {{ missingAuthorEvaluation.good_fit_reason }}
                      </p>
                    </div>

                    <div
                      v-if="
                        missingAuthorEvaluation.most_relevant_papers?.length > 0
                      "
                    >
                      <h5
                        class="text-[10px] uppercase tracking-widest font-bold text-gray-400 mb-2 flex items-center gap-2"
                      >
                        <i class="fas fa-file-lines text-emerald-500/70"></i>
                        Key Relevant Papers
                      </h5>
                      <ul class="space-y-2">
                        <li
                          v-for="(
                            paper, idx
                          ) in missingAuthorEvaluation.most_relevant_papers"
                          :key="idx"
                          class="flex items-start gap-2 group/paper"
                        >
                          <i
                            class="fas fa-arrow-right text-[10px] text-emerald-500 mt-1 opacity-0 group-hover/paper:opacity-100 transition-opacity"
                          ></i>
                          <span
                            class="text-xs text-gray-600 dark:text-gray-400"
                            >{{ paper.title }}</span
                          >
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div class="flex justify-center">
                  <button
                    class="text-xs text-emerald-600 hover:text-emerald-700 font-bold flex items-center gap-2 px-4 py-2 rounded-xl hover:bg-emerald-50 dark:hover:bg-emerald-900/10 transition-all"
                    @click="
                      missingAuthorEvaluation = null;
                      selectedMissingAuthor = null;
                      missingAuthorSearchQuery = '';
                    "
                  >
                    <i class="fas fa-rotate-left"></i>
                    Check another researcher
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Collaboration Opportunities View -->
        <div v-if="selectedSubTab === 'opportunities'" class="w-full">
          <div v-if="generatingCollaborationOpportunities" class="py-10 px-6">
            <status-bar
              :total-step-count="totalStepCount"
              :current-step-number="currentStepNumber"
              :status="status || 'Identifying collaboration opportunities...'"
              :substatus="substatus"
              theme="emerald"
            />
          </div>
          <div
            v-else-if="collaborationOpportunities === null"
            class="py-10 text-center bg-gray-50/50 dark:bg-gray-900/30 rounded-2xl border border-dashed border-gray-300 dark:border-gray-700"
          >
            <button
              class="bg-white dark:bg-gray-800 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-700 text-emerald-700 dark:text-emerald-400 px-6 py-2.5 uppercase text-xs font-bold rounded-xl transition-all shadow-sm flex items-center mx-auto gap-2"
              @click="generateCollaborationOpportunities()"
            >
              <i class="fas fa-wand-magic-sparkles"></i>
              Generate Collaboration Opportunities
            </button>
            <div
              class="mt-4 flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400 text-xs font-semibold italic"
            >
              <i class="fa-regular fa-clock-three text-emerald-500/70"></i>
              Usually takes about a minute
            </div>
          </div>
          <div
            v-else-if="collaborationOpportunities.length === 0"
            class="py-10 text-center bg-gray-50/50 dark:bg-gray-900/30 rounded-2xl border border-gray-200 dark:border-gray-800"
          >
            <div
              class="text-amber-600 dark:text-amber-400 mb-2 flex items-center justify-center gap-2"
            >
              <i class="fas fa-circle-info"></i>
              <span class="text-sm font-bold uppercase tracking-wider"
                >No Opportunities Identified</span
              >
            </div>
            <p
              class="text-sm text-gray-600 dark:text-gray-400 max-w-md mx-auto"
            >
              Based on the analysis, no specific collaboration opportunities or
              new project initiatives were identified for this query.
            </p>
          </div>
          <div v-else class="pb-4 space-y-6">
            <div
              v-for="(gap, index) in collaborationOpportunities"
              :key="index"
              class="bg-white/60 dark:bg-[#0d1a20]/60 backdrop-blur-xl border border-gray-300 dark:border-gray-700 shadow-xl rounded-2xl p-6 transition-all duration-300 hover:shadow-2xl"
            >
              <div class="flex justify-between items-start mb-4 gap-4">
                <div class="flex items-center gap-3">
                  <div
                    class="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 w-10 h-10 rounded-xl flex items-center justify-center shrink-0 shadow-sm"
                  >
                    <i
                      :class="[
                        'fas',
                        [
                          'fa-lightbulb-on',
                          'fa-microscope',
                          'fa-flask-vial',
                          'fa-dna',
                          'fa-atom',
                        ][index % 5],
                        'text-lg',
                      ]"
                    ></i>
                  </div>
                  <h4
                    class="text-xl font-headline font-bold text-gray-900 dark:text-white"
                  >
                    {{ gap.title }}
                  </h4>
                </div>
                <div class="flex items-center gap-2 shrink-0">
                  <feedback-modal
                    sentiment="POSITIVE"
                    item-type="COLLABORATION_OPPORTUNITY"
                    :item-id="`${question.hashedId}-${index}`"
                    :ancillary-information-label="gap.title"
                    :research-compass-hashed-id="
                      $route.params.researchCompassId
                    "
                  />
                  <feedback-modal
                    sentiment="NEGATIVE"
                    item-type="COLLABORATION_OPPORTUNITY"
                    :item-id="`${question.hashedId}-${index}`"
                    :ancillary-information-label="gap.title"
                    :research-compass-hashed-id="
                      $route.params.researchCompassId
                    "
                  />
                  <button
                    v-if="!isPublic && isCollaborationSpace"
                    class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all shadow-md flex items-center gap-2 whitespace-nowrap"
                    @click="draftCollaborationRequest(gap)"
                  >
                    <i class="fas fa-plus"></i>
                    Draft Request
                  </button>
                </div>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div
                    class="text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-1"
                  >
                    The Research Gap
                  </div>
                  <div
                    class="text-sm text-gray-700 dark:text-gray-300 leading-relaxed"
                  >
                    <research-compass-markdown-viewer
                      :text="gap.problemStatement"
                      :sources="question.sources"
                      :question="question"
                      ref-name="sourceHolder"
                      @scroll-to-citation="scrollToExpert"
                    />
                  </div>
                </div>
                <div>
                  <div
                    class="text-[10px] font-bold uppercase tracking-widest text-blue-600 dark:text-blue-400 mb-1"
                  >
                    Proposed Initiative
                  </div>
                  <div
                    class="text-sm text-gray-700 dark:text-gray-300 leading-relaxed"
                  >
                    <research-compass-markdown-viewer
                      :text="gap.proposedSolution"
                      :sources="question.sources"
                      :question="question"
                      ref-name="sourceHolder"
                      @scroll-to-citation="scrollToExpert"
                    />
                  </div>
                </div>
              </div>

              <div v-if="gap.relevantResearchers?.length" class="mt-6">
                <div
                  class="text-[10px] font-bold uppercase tracking-widest text-emerald-600 dark:text-emerald-400 mb-3"
                >
                  Qualified Experts
                </div>
                <div class="flex flex-wrap gap-4">
                  <div
                    v-for="(researcher, rIdx) in gap.relevantResearchers"
                    :key="rIdx"
                    class="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800/50 rounded-xl p-3 flex-1 min-w-[250px]"
                  >
                    <div
                      class="font-bold text-sm text-emerald-800 dark:text-emerald-300 mb-1"
                    >
                      <research-compass-markdown-viewer
                        :text="`[${researcher.name.replace(/^\[|\]$/g, '')}]`"
                        :question="question"
                        ref-name="authorHolder"
                        @scroll-to-citation="scrollToExpert"
                      />
                    </div>
                    <div
                      class="text-xs text-gray-600 dark:text-gray-400 italic"
                    >
                      <research-compass-markdown-viewer
                        :text="researcher.rationale"
                        :sources="question.sources"
                        :question="question"
                        ref-name="authorHolder"
                        @scroll-to-citation="scrollToExpert"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </transition>
  </div>
</template>

<script>
import * as utils from "../../utils";
import AuthorCard from "./AuthorCard.vue";
import StatusBar from "./StatusBar.vue";
import ResearchCompassMarkdownViewer from "./ResearchCompassMarkdownViewer.vue";
import FeedbackModal from "./FeedbackModal.vue";
import ResearchCompassErrorCard from "./ResearchCompassErrorCard.vue";

const { resolveRcApiUrl, keysToSnake } = utils;

export default {
  components: {
    AuthorCard,
    StatusBar,
    ResearchCompassMarkdownViewer,
    FeedbackModal,
    ResearchCompassErrorCard,
  },
  props: {
    question: Object,
    loadedAuthors: Boolean,
    loadingAuthors: Boolean,
    includeResearchers: Boolean,
    selectedSubTab: String,
    generatingCollaborationOpportunities: Boolean,
  },
  emits: [
    "start-loading",
    "finish-loading",
    "cancel-loading",
    "reload-research-compass",
    "change-sub-tab",
    "get-author-evaluations",
    "update:generating-narrative",
    "update:generating-table",
    "update:generating-opportunities",
    "draft-collaboration-request",
  ],
  data() {
    return {
      allAuthorsExpanded: false,
      totalStepCount: 0,
      currentStepNumber: 0,
      status: "",
      substatus: "",
      researchCompassQuestionChannel: null,
      generatingNarrative: false,
      generatingTable: false,
      missingAuthorSearchQuery: "",
      isSearchingMissingAuthor: false,
      missingAuthorSearchResults: [],
      missingAuthorSearchStatus: null,
      selectedMissingAuthor: null,
      isEvaluatingMissingAuthor: false,
      missingAuthorEvaluation: null,
    };
  },
  computed: {
    isFailed() {
      const status = this.question.authorsStatus;
      return !!(status?.hasError || status?.message === "Error");
    },
    orderedAuthors() {
      if (!this.question?.authors) return [];
      return Object.values(this.question.authors).sort((a, b) => {
        const scoreA = a.normalizedFitScore || 0;
        const scoreB = b.normalizedFitScore || 0;
        if (scoreA !== scoreB) {
          return scoreB - scoreA;
        }
        return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
      });
    },
    orderedAuthorsRecommended() {
      return this.orderedAuthors.filter(
        (author) => author.normalizedFitScore > 80,
      );
    },
    orderedAuthorsNotRecommended() {
      return this.orderedAuthors.filter(
        (author) => author.normalizedFitScore <= 80,
      );
    },
    narrative() {
      const answers = this.question.answers || [];
      const narrative = answers
        .filter(
          (answer) =>
            (answer.answerType?.value || answer.answerType) ===
            "AUTHOR_NARRATIVE",
        )
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      return narrative.length > 0 ? narrative[0].content : null;
    },
    table() {
      const answers = this.question.answers || [];
      const table = answers
        .filter(
          (answer) =>
            (answer.answerType?.value || answer.answerType) === "AUTHOR_TABLE",
        )
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      return table.length > 0 ? table[0].content : null;
    },
    collaborationOpportunities() {
      const answers = this.question.answers || [];
      const opportunitiesAnswer = answers
        .filter(
          (answer) =>
            (answer.answerType?.value || answer.answerType) ===
            "COLLABORATION_OPPORTUNITIES",
        )
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      if (opportunitiesAnswer.length > 0) {
        try {
          const { content } = opportunitiesAnswer[0];
          if (!content || content === "[]") return [];
          const parsed = JSON.parse(content);
          return utils.keysToCamel(parsed);
        } catch (e) {
          console.error("Failed to parse collaboration opportunities JSON", e);
          return [];
        }
      }
      return null;
    },
    isPublic() {
      return Boolean(this.$route.params.viewKey);
    },
    isCollaborationSpace() {
      return Boolean(this.$route.params.collaborationSpaceId);
    },
    activeAxios() {
      if (this.isPublic) {
        return this.$api.axiosPublic;
      }
      return this.$api.axios;
    },
  },
  watch: {
    generatingNarrative(newVal) {
      this.$emit("update:generating-narrative", newVal);
    },
    generatingTable(newVal) {
      this.$emit("update:generating-table", newVal);
    },
    narrative(newVal) {
      if (newVal) {
        this.generatingNarrative = false;
      }
    },
    table(newVal) {
      if (newVal) {
        this.generatingTable = false;
      }
    },
    collaborationOpportunities(newVal) {
      if (newVal) {
        this.$emit("update:generating-opportunities", false);
      }
    },
  },
  mounted() {
    this.setUpChannel(this.question.hashedId);
  },
  methods: {
    toggleAllAuthorsExpanded() {
      this.allAuthorsExpanded = !this.allAuthorsExpanded;
    },
    searchMissingAuthor() {
      if (!this.missingAuthorSearchQuery.trim()) return;

      this.isSearchingMissingAuthor = true;
      this.missingAuthorSearchResults = [];
      this.missingAuthorSearchStatus = "Searching...";
      this.selectedMissingAuthor = null;
      this.missingAuthorEvaluation = null;

      this.activeAxios
        .get(
          `/research-compass-data/author/search/?query=${encodeURIComponent(
            this.missingAuthorSearchQuery,
          )}`,
        )
        .then((resp) => {
          const { data } = resp;
          this.isSearchingMissingAuthor = false;
          if (data && data.length > 0) {
            this.missingAuthorSearchStatus = `Found ${data.length} result${
              data.length !== 1 ? "s" : ""
            }`;
            this.missingAuthorSearchResults = data.map((result) => ({
              name: result.name,
              authorId: result.authorid,
              url: `https://www.semanticscholar.org/author/${result.authorid}`,
              affiliation: result.fields_of_study
                ? result.fields_of_study.join(", ")
                : "No fields provided",
              papercount: result.papercount,
              citationcount: result.citationcount,
              minYear: result.min_year,
              maxYear: result.max_year,
              recentPapers: result.recent_papers
                ? result.recent_papers.slice(0, 3)
                : [],
            }));
          } else {
            this.missingAuthorSearchStatus = `No results found for "${this.missingAuthorSearchQuery}".`;
          }
        })
        .catch(() => {
          this.isSearchingMissingAuthor = false;
          this.missingAuthorSearchStatus = "Error searching Semantic Scholar.";
        });
    },
    selectMissingAuthor(author) {
      this.selectedMissingAuthor = author;
      this.missingAuthorSearchResults = [];
      this.missingAuthorSearchStatus = null;
      this.evaluateMissingAuthor();
    },
    evaluateMissingAuthor() {
      if (!this.selectedMissingAuthor) return;

      this.isEvaluatingMissingAuthor = true;
      this.missingAuthorEvaluation = null;

      this.status = "Initializing researcher evaluation...";
      this.substatus = "";
      this.totalStepCount = 10;
      this.currentStepNumber = 1;

      const url = resolveRcApiUrl(
        this.question.hashedId,
        "evaluate-specific-author",
        this.$route.params,
      );

      this.activeAxios
        .post(
          url,
          keysToSnake({
            authorId: String(this.selectedMissingAuthor.authorId),
            recommendedExperts: this.question.authors || {},
            updateChannelName: `${this.question.hashedId}missingauthor`,
          }),
        )
        .then((resp) => {
          this.missingAuthorEvaluation = resp.data;
          this.isEvaluatingMissingAuthor = false;
        })
        .catch(() => {
          this.isEvaluatingMissingAuthor = false;
          this.$toast.error("Error evaluating researcher.");
        });
    },
    scrollToExpert(authorName) {
      this.$emit("change-sub-tab", "list");
      nextTick(() => {
        // Find the index of this author in our combined ordered list
        const index = this.orderedAuthors.findIndex(
          (a) => a.name.toLowerCase() === authorName.toLowerCase(),
        );

        if (index !== -1) {
          const refId = `expert${index + 1}`;
          const ref = this.$refs[refId];
          const el = Array.isArray(ref) ? ref[0].$el : ref?.$el;
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }
      });
    },
    setUpChannel(hashedId) {
      if (this.researchCompassQuestionChannel === null) {
        const prefix = import.meta.env.VITE_ENV_CONFIG_NAMESPACE || "";
        const baseChannel = this.$ably.channels.get(
          `${prefix}researchcompassquestion${hashedId}`,
        );
        const authorChannel = this.$ably.channels.get(
          `${prefix}researchcompassquestion${hashedId}authors`,
        );
        const missingAuthorChannel = this.$ably.channels.get(
          `${prefix}researchcompassquestion${hashedId}missingauthor`,
        );

        const self = this;
        const handleMessage = (message) => {
          let data;
          try {
            data =
              typeof message.data === "string"
                ? JSON.parse(message.data)
                : message.data;
          } catch (e) {
            console.error("Failed to parse websocket message", e);
            return;
          }

          if (!data) return;

          // Data from Headllamp is nested in metadata, Notedsource sends it directly
          const statusData = data.metadata || data;

          if (statusData.status !== "Error") {
            if (statusData.status) self.status = statusData.status;
            if (statusData.substatus !== undefined)
              self.substatus = statusData.substatus;

            if (statusData.totalStepCount)
              self.totalStepCount = statusData.totalStepCount;
            if (statusData.currentStepNumber)
              self.currentStepNumber = statusData.currentStepNumber;

            if (statusData.reload) {
              self.$emit("reload-research-compass");
            }
            if (statusData.finished) {
              if (!self.loadedAuthors && !self.isEvaluatingMissingAuthor) {
                self.$emit("finish-loading", "authors");
              }
            }
          } else {
            // Handle error state
            self.generatingNarrative = false;
            self.generatingTable = false;
            self.isEvaluatingMissingAuthor = false;
            if (statusData.reload) {
              self.$emit("reload-research-compass");
            }
            self.$emit("cancel-loading", "authors");
          }
        };

        baseChannel.subscribe(handleMessage);
        authorChannel.subscribe((message) => {
          handleMessage(message);
          let data;
          try {
            data =
              typeof message.data === "string"
                ? JSON.parse(message.data)
                : message.data;
          } catch (e) {
            return;
          }
          const statusData = data?.metadata || data;
          if (statusData?.status && !self.loadedAuthors) {
            self.$emit("start-loading", "authors");
          }
        });
        missingAuthorChannel.subscribe(handleMessage);

        this.researchCompassQuestionChannel = baseChannel;
        this.authorChannel = authorChannel;
        this.missingAuthorChannel = missingAuthorChannel;
      }
    },
    getAuthorEvaluations() {
      this.status = "Initializing expert hydration...";
      this.substatus = "";
      this.totalStepCount = 10;
      this.currentStepNumber = 1;
      this.$emit("get-author-evaluations");
    },
    generateNarrative() {
      this.generatingNarrative = true;
      this.status = "Initializing explanation...";
      this.substatus = "";
      this.totalStepCount = 10;
      this.currentStepNumber = 1;
      const url = resolveRcApiUrl(
        this.question.hashedId,
        "generate-author-narrative",
        this.$route.params,
      );

      this.activeAxios.post(url).catch(() => {
        this.generatingNarrative = false;
      });
    },
    clearNarrative() {
      const url = resolveRcApiUrl(
        this.question.hashedId,
        "clear-author-narrative",
        this.$route.params,
      );

      this.activeAxios.post(url).then(() => {
        this.$emit("reload-research-compass");
        this.generateNarrative();
      });
    },
    generateTable() {
      this.generatingTable = true;
      this.status = "Initializing comparison...";
      this.substatus = "";
      this.totalStepCount = 10;
      this.currentStepNumber = 1;
      const url = resolveRcApiUrl(
        this.question.hashedId,
        "generate-author-table",
        this.$route.params,
      );

      this.activeAxios.post(url).catch(() => {
        this.generatingTable = false;
      });
    },
    generateCollaborationOpportunities() {
      this.$emit("update:generating-opportunities", true);
      this.status = "Identifying collaboration opportunities...";
      this.substatus = "";
      this.totalStepCount = 10;
      this.currentStepNumber = 1;
      const url = resolveRcApiUrl(
        this.question.hashedId,
        "generate-collaboration-opportunities",
        this.$route.params,
      );

      this.activeAxios.post(url).catch(() => {
        this.$emit("update:generating-opportunities", false);
      });
    },
    draftCollaborationRequest(gap) {
      const { title } = gap;
      const questionText = this.question.question;
      const compassTitle =
        this.question.researchCompass?.title || "Research Compass Project";

      let description = `${gap.problemStatement}\n\nProposed Solution: ${gap.proposedSolution}`;

      description += "\n\n---\n**Context from Research Compass**\n";
      description += `**Compass Project:** ${compassTitle}\n`;
      description += `**Original Question:** ${questionText}\n`;

      if (gap.relevantResearchers?.length) {
        description += "\n**Proposed Experts:**\n";
        gap.relevantResearchers.forEach((r) => {
          const name = r.name.replace(/^\[|\]$/g, "");
          const idStr = r.id ? ` (ID: ${r.id})` : "";
          description += `- ${name}${idStr}\n`;
        });
      }

      const initialData = {
        title,
        description,
        engagementTimeline: gap.engagementTimeline || "",
        engagementDuration: gap.engagementDuration || "",
        deliverableTypes: gap.deliverableTypes || [],
        requiresLabResources: gap.requiresLabResources || "",
        geographicRestriction: "GLOBAL",
        collaboratorTypes: ["RESEARCH"],
      };

      this.$emit("draft-collaboration-request", initialData);
    },
  },
};
</script>
